import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { usePermanentRealtimeDealers } from "components/DealersAndLocationsDropdown/hooks";
import { usePermanentRealTimeUser } from "hooks";
import { ROLES } from "models";

import type { DropdownItemProps } from "components/Dropdown";

const MAX_DEALER_NAME_CHARS = 3;

interface DealerLocationGroupedDropdownOption extends DropdownItemProps<string> {
  icon: string;
  text: string;
  labelText: string;
  isLastLocation?: boolean;
  dealer_id: number;
  location_id: number;
}

type UseDealerAndLocationsGroupedOptionsProps = {
  selectAll: boolean;
  searchTerm?: string;
};

export const useDealerAndLocationsGroupedOptions = ({ searchTerm, selectAll }: UseDealerAndLocationsGroupedOptionsProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { dealers } = usePermanentRealtimeDealers();

  const dealerAndLocationsGroupedOptions = useMemo(() => {
    const options: DealerLocationGroupedDropdownOption[] = [];

    const activeDealers = dealers.filter(dealer => dealer.active);
    if (!activeDealers.length) return options;

    const lowerCaseSearchTerm = searchTerm?.toLowerCase();

    activeDealers
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(dealer => {
        const { name: dealerName, id: dealerId } = dealer;

        const userLocations =
          user?.my_locations && user.my_locations.length > 0 ? dealer.locations?.filter(loc => user.my_locations?.includes(loc.id)) : dealer.locations;
        const activeLocations = userLocations?.filter(location => location.active) || [];

        const isDealerMatch = lowerCaseSearchTerm ? dealerName.toLowerCase().includes(lowerCaseSearchTerm) : false;
        const filteredLocations = lowerCaseSearchTerm ? activeLocations.filter(location => location.name.toLowerCase().includes(lowerCaseSearchTerm)) : activeLocations;

        if (isDealerMatch) {
          options.push({
            icon: "house",
            disabled: true,
            key: `dealer-${dealerId}`,
            value: `dealer-${dealerId}`,
            labelText: dealerName,
            text: dealerName,
            dealer_id: dealerId,
            location_id: 0
          });

          if (selectAll && activeLocations.length > 0 && user && [ROLES.CallCenter, ROLES.WarrantyManager].includes(user.role_id)) {
            options.push({
              icon: "location dot",
              disabled: false,
              key: "all",
              value: `all-${dealerId}`,
              labelText: dealerName,
              text: t("v8_all_locations", "All locations"),
              dealer_id: dealerId,
              location_id: 0
            });
          }

          activeLocations
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach((loc, index) => {
              options.push({
                icon: "location dot regular",
                text: loc.name,
                labelText: `${dealerName.substring(0, MAX_DEALER_NAME_CHARS)} - ${loc.name}`,
                key: `location-${loc.id}`,
                value: `location-${loc.id}`,
                disabled: false,
                isLastLocation: index === activeLocations.length - 1,
                dealer_id: dealerId,
                location_id: loc.id
              });
            });
        } else if (filteredLocations.length > 0) {
          options.push({
            icon: "house",
            disabled: true,
            key: `dealer-${dealerId}`,
            value: `dealer-${dealerId}`,
            labelText: dealerName,
            text: dealerName,
            dealer_id: dealerId,
            location_id: 0
          });

          filteredLocations
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach((loc, index) => {
              options.push({
                icon: "location dot regular",
                text: loc.name,
                labelText: `${dealerName.substring(0, MAX_DEALER_NAME_CHARS)} - ${loc.name}`,
                key: `location-${loc.id}`,
                value: `location-${loc.id}`,
                disabled: false,
                isLastLocation: index === filteredLocations.length - 1,
                dealer_id: dealerId,
                location_id: loc.id
              });
            });
        }
      });

    return options;
  }, [dealers, searchTerm, user]);

  return dealerAndLocationsGroupedOptions;
};
